import { Suspense, lazy, useEffect } from "react";


import { HeroSection } from "@/components/home/HeroSection";
import { SEOHead, OrganizationJsonLd, WebSiteJsonLd } from "@/components/seo";
import { Loader2 } from "lucide-react";

// Lazy load below-the-fold components to reduce initial bundle size
// We keep the factory functions to reuse them for preloading
const StepsSectionImport = () => import("@/components/home/StepsSection").then(module => ({ default: module.StepsSection }));
const TreatmentsSectionImport = () => import("@/components/home/TreatmentsSection").then(module => ({ default: module.TreatmentsSection }));
const PricingComparisonSectionImport = () => import("@/components/home/PricingComparisonSection").then(module => ({ default: module.PricingComparisonSection }));
const HowItWorksSectionImport = () => import("@/components/home/HowItWorksSection").then(module => ({ default: module.HowItWorksSection }));
const TrustSectionImport = () => import("@/components/home/TrustSection").then(module => ({ default: module.TrustSection }));
const TestimonialsSectionImport = () => import("@/components/home/TestimonialsSection").then(module => ({ default: module.TestimonialsSection }));
const HomeFAQSectionImport = () => import("@/components/home/HomeFAQSection").then(module => ({ default: module.HomeFAQSection }));
const CTASectionImport = () => import("@/components/home/CTASection").then(module => ({ default: module.CTASection }));

const StepsSection = lazy(StepsSectionImport);
const TreatmentsSection = lazy(TreatmentsSectionImport);
const PricingComparisonSection = lazy(PricingComparisonSectionImport);
const HowItWorksSection = lazy(HowItWorksSectionImport);
const TrustSection = lazy(TrustSectionImport);
const TestimonialsSection = lazy(TestimonialsSectionImport);
const HomeFAQSection = lazy(HomeFAQSectionImport);
const CTASection = lazy(CTASectionImport);

const SectionLoader = () => (
  <div className="w-full h-[50vh] flex items-center justify-center" style={{ backgroundColor: 'rgba(245, 245, 245, 0.2)' }}>
    <Loader2 className="w-8 h-8 animate-spin text-primary/50" />
  </div>
);

const Index = () => {
  // Idle Preloading: Start fetching components when the browser is idle
  useEffect(() => {
    const preloadParams = [
      StepsSectionImport,
      TreatmentsSectionImport,
      HowItWorksSectionImport,
      PricingComparisonSectionImport,
      TrustSectionImport,
      TestimonialsSectionImport,
      HomeFAQSectionImport,
      CTASectionImport
    ];

    const preloadNext = (index: number) => {
      if (index >= preloadParams.length) return;
      
      const importFn = preloadParams[index];
      // Use requestIdleCallback if available, or fallback to setTimeout
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => {
            importFn().finally(() => preloadNext(index + 1));
        });
      } else {
        setTimeout(() => {
            importFn().finally(() => preloadNext(index + 1));
        }, 100); // Small delay between chunks
      }
    };

    // Start preloading immediately (but deferred by requestIdleCallback)
    preloadNext(0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* SEO Meta Tags & Structured Data */}
      <SEOHead
        title="Medical Tourism India | Save 70% | TrustedHeal"
        description="Discover world-class, cost-effective medical treatments in India with TrustedHeal. Access JCI-accredited hospitals, top doctors, and save up to 70% on your healthcare journey."
        canonicalPath="/"
        keywords="medical tourism India, dental implants India, healthcare India, medical travel, affordable healthcare, dental tourism, All-on-4 implants India, trustedheal, international patients India, JCI accredited hospitals India, cardiac surgery India, orthopedic surgery India"
      />
      <OrganizationJsonLd />
      <WebSiteJsonLd />


      <main>
        {/* Hero Section - Kept Eager for LCP */}
        <HeroSection />

        <Suspense fallback={<SectionLoader />}>
          <div className="animate-in-fade">
            <StepsSection />
          </div>
        </Suspense>

        <Suspense fallback={<SectionLoader />}>
          <div className="animate-in-fade">
            <TreatmentsSection />
          </div>
        </Suspense>

        <Suspense fallback={<SectionLoader />}>
          <div className="animate-in-fade">
            <HowItWorksSection />
          </div>
        </Suspense>

        <Suspense fallback={<SectionLoader />}>
          <div className="animate-in-fade">
            <PricingComparisonSection />
          </div>
        </Suspense>
        
        <Suspense fallback={<SectionLoader />}>
          <div className="animate-in-fade">
            <TrustSection />
          </div>
        </Suspense>

        <Suspense fallback={<SectionLoader />}>
          <div className="animate-in-fade">
            <TestimonialsSection />
          </div>
        </Suspense>

        <Suspense fallback={<SectionLoader />}>
          <div className="animate-in-fade">
            <HomeFAQSection />
          </div>
        </Suspense>

        <Suspense fallback={<SectionLoader />}>
          <div className="animate-in-fade">
            <CTASection />
          </div>
        </Suspense>
      </main>


    </div>
  );
};

export default Index;

